import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Icon from '../components/Icon';
import {
  fetchDocuments, fetchDocumentPreviewUrl,
  fetchInvestors, fetchInvestor, patchInvestor,
  createPacket, fetchAccessLog,
} from '../services/api';
import { usePagePermissions } from '../hooks/usePagePermissions';

// ── helpers ──
const fmtBytes = (b) => {
  if (!b && b !== 0) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');
const fmtAgo = (iso) => {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const STATUS_BADGE = {
  not_started:  { cls: 'badge-muted',   label: 'Not started' },
  queued:       { cls: 'badge-info',    label: 'Queued' },
  sent:         { cls: 'badge-warning', label: 'Sent' },
  replied:      { cls: 'badge-info',    label: 'Replied' },
  meeting:      { cls: 'badge-success', label: 'Meeting' },
  pass:         { cls: 'badge-muted',   label: 'Pass' },
  invested:     { cls: 'badge-success', label: 'Invested' },
  cold_dropped: { cls: 'badge-muted',   label: 'Cold dropped' },
};

// ── Library tab ──
const Library = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewName, setPreviewName] = useState('');
  const [previewMime, setPreviewMime] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchDocuments()
      .then((d) => { if (!cancelled) setDocs(d.documents || []); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.error || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const grouped = useMemo(() => {
    const m = new Map();
    for (const d of docs) {
      if (!m.has(d.category)) m.set(d.category, []);
      m.get(d.category).push(d);
    }
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [docs]);

  const onPreview = async (doc) => {
    try {
      const r = await fetchDocumentPreviewUrl(doc.id);
      setPreviewUrl(r.url);
      setPreviewName(r.filename);
      setPreviewMime(r.mime);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const onDownload = async (doc) => {
    try {
      const r = await fetchDocumentPreviewUrl(doc.id);
      window.open(r.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  return (
    <>
      {error && <div role="alert" style={errorStyle}>{error}</div>}
      {loading ? (
        <div className="col col-gap-2">{[0, 1, 2].map((i) => <div key={i} className="skeleton skeleton-block" style={{ height: 60 }} />)}</div>
      ) : grouped.length === 0 ? (
        <div className="empty">
          <div className="empty-icon"><Icon name="folder" size={26} /></div>
          <h3>No documents yet</h3>
          <p>Run scripts/seed-data-room.js locally to upload the Tier 0/1 file tree.</p>
        </div>
      ) : (
        <div className="col col-gap-4">
          {grouped.map(([cat, files]) => (
            <div key={cat}>
              <div className="card-title" style={{ marginBottom: 'var(--s2)' }}>{cat}</div>
              <div className="col col-gap-2">
                {files.map((d) => (
                  <div key={d.id} className="row row-gap-3" style={{ padding: 'var(--s3) var(--s4)', background: 'var(--bg-elevated)', border: '1px solid var(--bd)', borderRadius: 'var(--r-md)', justifyContent: 'space-between' }}>
                    <div className="col" style={{ flex: 1, minWidth: 0 }}>
                      <div className="font-semibold text-sm" style={{ color: 'var(--tx1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.filename}</div>
                      <div className="text-xs" style={{ color: 'var(--tx3)' }}>{fmtBytes(d.size_bytes)} · v{d.version} · updated {fmtDate(d.last_updated)}</div>
                    </div>
                    <div className="row row-gap-2">
                      {d.mime === 'application/pdf' && (
                        <button type="button" className="btn btn-ghost" onClick={() => onPreview(d)}>Preview</button>
                      )}
                      <button type="button" className="btn btn-outline" onClick={() => onDownload(d)}>
                        <Icon name="download" size={14} /> Download
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {previewUrl && (
        <Modal onClose={() => setPreviewUrl(null)} title={previewName}>
          {previewMime === 'application/pdf' ? (
            <iframe title={previewName} src={previewUrl} style={{ width: '100%', height: '70vh', border: '1px solid var(--bd)', borderRadius: 'var(--r-md)' }} />
          ) : (
            <div className="text-sm" style={{ color: 'var(--tx2)' }}>This file type is not previewable in-browser. Use the Download button to save it locally.</div>
          )}
        </Modal>
      )}
    </>
  );
};

// ── Modal ──
const Modal = ({ children, onClose, title, width = 720 }) => (
  <div role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined} onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 'var(--s6)', overflow: 'auto' }}>
    <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--bg-canvas)', border: '1px solid var(--bd)', borderRadius: 'var(--r-lg)', padding: 'var(--s5)', width: '100%', maxWidth: width, marginTop: 'var(--s4)' }}>
      <div className="row row-gap-3" style={{ justifyContent: 'space-between', marginBottom: 'var(--s4)' }}>
        <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--tx1)' }}>{title}</h2>
        <button type="button" className="icon-btn" onClick={onClose} aria-label="Close"><Icon name="x" size={18} /></button>
      </div>
      {children}
    </div>
  </div>
);

// ── Send packet modal ──
const SendPacketModal = ({ investor, docs, onClose, onCreated }) => {
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const grouped = useMemo(() => {
    const m = new Map();
    for (const d of docs) {
      if (!m.has(d.category)) m.set(d.category, []);
      m.get(d.category).push(d);
    }
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [docs]);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleCategory = (cat) => setSelected((s) => {
    const n = new Set(s);
    const ids = (grouped.find(([c]) => c === cat)?.[1] || []).map((d) => d.id);
    const allOn = ids.every((id) => n.has(id));
    if (allOn) ids.forEach((id) => n.delete(id));
    else ids.forEach((id) => n.add(id));
    return n;
  });

  const firstName = (investor.primary_contact_name || investor.name || '').split(' ')[0];

  const create = async () => {
    setBusy(true);
    try {
      const ids = Array.from(selected);
      const draft = `Hi ${firstName || 'there'}, here are the materials we discussed: <link will go here> — link expires in 7 days. — Stanford`;
      const r = await createPacket({
        investor_id: investor.id,
        document_ids: ids,
        outreach_msg: draft,
      });
      const message = `Hi ${firstName || 'there'},\n\nHere are the Coynitt materials we discussed:\n${r.url}\n\nThis link is private to you and expires in 7 days. Happy to walk through anything by email or a quick call.\n\nThanks,\nStanford`;
      try { await navigator.clipboard.writeText(message); } catch (e) { /* clipboard may be denied */ }
      setResult({ url: r.url, message });
      onCreated?.();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <Modal onClose={onClose} title={`Packet ready for ${investor.name}`} width={640}>
        <div className="col col-gap-3">
          <div className="text-sm" style={{ color: 'var(--tx2)' }}>The outreach message has been copied to your clipboard. Paste it into your email client.</div>
          <div className="card" style={{ background: 'var(--bg-elevated)' }}>
            <div className="text-xs" style={{ color: 'var(--tx3)', marginBottom: 4 }}>SIGNED LINK (7-day expiry)</div>
            <code style={{ wordBreak: 'break-all', color: 'var(--teal)', fontSize: 13 }}>{result.url}</code>
          </div>
          <pre style={{ background: 'var(--bg-canvas)', border: '1px solid var(--bd)', borderRadius: 'var(--r-md)', padding: 'var(--s4)', whiteSpace: 'pre-wrap', fontFamily: 'var(--font)', fontSize: 13, color: 'var(--tx1)', margin: 0 }}>{result.message}</pre>
          <div className="row row-gap-3" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-outline" onClick={() => navigator.clipboard.writeText(result.message)}><Icon name="copy" size={14} /> Copy message</button>
            <button type="button" className="btn btn-primary" onClick={onClose}>Done</button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} title={`Send packet to ${investor.name}`} width={640}>
      <div className="col col-gap-4">
        <div className="text-sm" style={{ color: 'var(--tx2)' }}>
          Pick the documents to bundle into this investor's signed link. The link expires in 7 days. Each access is logged.
        </div>
        {error && <div role="alert" style={errorStyle}>{error}</div>}
        <div style={{ maxHeight: '50vh', overflow: 'auto', border: '1px solid var(--bd)', borderRadius: 'var(--r-md)', padding: 'var(--s3)' }}>
          {grouped.map(([cat, files]) => (
            <div key={cat} style={{ marginBottom: 'var(--s3)' }}>
              <button type="button" onClick={() => toggleCategory(cat)} className="text-sm font-semibold" style={{ background: 'transparent', color: 'var(--tx1)', padding: 'var(--s2) 0', cursor: 'pointer' }}>
                {cat} ({files.length})
              </button>
              <div className="col col-gap-2" style={{ paddingLeft: 'var(--s4)' }}>
                {files.map((d) => (
                  <label key={d.id} className="row row-gap-2" style={{ cursor: 'pointer' }}>
                    <input type="checkbox" checked={selected.has(d.id)} onChange={() => toggle(d.id)} />
                    <span className="text-sm" style={{ color: 'var(--tx2)' }}>{d.filename}</span>
                    <span className="text-xs" style={{ color: 'var(--tx3)', marginLeft: 'auto' }}>{fmtBytes(d.size_bytes)}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="row row-gap-3" style={{ justifyContent: 'space-between' }}>
          <div className="text-sm" style={{ color: 'var(--tx3)' }}>{selected.size} document{selected.size === 1 ? '' : 's'} selected</div>
          <div className="row row-gap-2">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="button" className="btn btn-primary" disabled={busy || selected.size === 0} onClick={create}>
              <Icon name="send" size={14} /> Generate signed link
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

// ── Investor detail drawer ──
const InvestorDetail = ({ investorId, docs, onClose, onChanged, isSuperAdmin }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showSend, setShowSend] = useState(false);

  const reload = useCallback(() => {
    if (!investorId) return;
    fetchInvestor(investorId).then(setData).catch((e) => setError(e.response?.data?.error || e.message));
  }, [investorId]);

  useEffect(() => { reload(); }, [reload]);

  if (!investorId) return null;

  const onPatch = async (patch) => {
    setBusy(true);
    try {
      const r = await patchInvestor(investorId, patch);
      setData((d) => ({ ...d, ...r }));
      onChanged?.();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <Modal onClose={onClose} title="Loading…">{error || 'Loading investor…'}</Modal>;

  return (
    <Modal onClose={onClose} title={data.name} width={760}>
      <div className="col col-gap-4">
        <div className="row row-gap-3" style={{ flexWrap: 'wrap' }}>
          <span className="badge badge-info">{data.tier || 'Untiered'}</span>
          <span className={`badge ${(STATUS_BADGE[data.outreach_status] || STATUS_BADGE.not_started).cls}`}>
            {(STATUS_BADGE[data.outreach_status] || STATUS_BADGE.not_started).label}
          </span>
          {data.last_engaged && <span className="badge badge-success">Last engaged {fmtAgo(data.last_engaged)}</span>}
        </div>

        {error && <div role="alert" style={errorStyle}>{error}</div>}

        <div className="responsive-2col-tight">
          <Field label="Type">{data.type}</Field>
          <Field label="Fund stage">{data.fund_stage}</Field>
          <Field label="Geography">{data.geography}</Field>
          <Field label="Avg check size">{data.avg_check_size}</Field>
          <Field label="Sector fit">{data.sector_fit}</Field>
          <Field label="Crypto / MSB OK?">{data.crypto_msb_ok}</Field>
          <Field label="Lead capable">{data.lead_capable}</Field>
          <Field label="Best contact method">{data.best_contact_method}</Field>
          <Field label="Primary contact">{data.primary_contact_name}{data.title ? ` · ${data.title}` : ''}</Field>
          <Field label="Email">{data.email || '—'}</Field>
          <Field label="LinkedIn">{data.linkedin}</Field>
          <Field label="Twitter / X">{data.twitter}</Field>
          <Field label="Website">{data.website}</Field>
          <Field label="Submission form">{data.submission_form}</Field>
          <Field label="Date contacted">{fmtDate(data.date_contacted)}</Field>
          <Field label="Warm path">{data.warm_path}</Field>
        </div>

        <div>
          <div className="card-title" style={{ marginBottom: 'var(--s2)' }}>Outreach status</div>
          <select
            value={data.outreach_status || 'not_started'}
            disabled={!isSuperAdmin || busy}
            onChange={(e) => onPatch({ outreach_status: e.target.value })}
            style={selectStyle}
          >
            {Object.entries(STATUS_BADGE).map(([k, v]) => (<option key={k} value={k}>{v.label}</option>))}
          </select>
        </div>

        <div>
          <div className="card-title" style={{ marginBottom: 'var(--s2)' }}>Notes / outreach log</div>
          <textarea
            defaultValue={data.notes || ''}
            disabled={!isSuperAdmin}
            onBlur={(e) => { if (e.target.value !== (data.notes || '')) onPatch({ notes: e.target.value }); }}
            style={{ ...selectStyle, width: '100%', minHeight: 160, fontFamily: 'var(--font-mono, monospace)', cursor: 'text' }}
          />
        </div>

        <div>
          <div className="card-title" style={{ marginBottom: 'var(--s2)' }}>Packet history</div>
          {(!data.packets || data.packets.length === 0) ? (
            <div className="text-sm" style={{ color: 'var(--tx3)' }}>No packets sent yet.</div>
          ) : (
            <div className="col col-gap-2">
              {data.packets.map((p) => {
                const expired = new Date(p.expires_at).getTime() < Date.now();
                return (
                  <div key={p.id} className="row row-gap-3" style={{ padding: 'var(--s2) var(--s3)', background: 'var(--bg-elevated)', border: '1px solid var(--bd)', borderRadius: 'var(--r-sm)', justifyContent: 'space-between' }}>
                    <div className="col">
                      <div className="text-sm" style={{ color: 'var(--tx1)' }}>{p.document_ids.length} document{p.document_ids.length === 1 ? '' : 's'}</div>
                      <div className="text-xs" style={{ color: 'var(--tx3)' }}>Created {fmtDate(p.created_at)} · {expired ? 'Expired' : `Expires ${fmtDate(p.expires_at)}`}</div>
                    </div>
                    <span className={`badge ${expired ? 'badge-muted' : 'badge-success'}`}>{expired ? 'Expired' : 'Active'}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <div className="card-title" style={{ marginBottom: 'var(--s2)' }}>Recent access</div>
          {(!data.access_log || data.access_log.length === 0) ? (
            <div className="text-sm" style={{ color: 'var(--tx3)' }}>No access yet.</div>
          ) : (
            <div className="col col-gap-1">
              {data.access_log.slice(0, 10).map((r) => (
                <div key={r.id} className="row row-gap-3" style={{ fontSize: 12, color: 'var(--tx2)' }}>
                  <span style={{ minWidth: 130, color: 'var(--tx3)' }}>{fmtAgo(r.accessed_at)}</span>
                  <span className="badge badge-info">{r.event}</span>
                  <span style={{ color: 'var(--tx3)' }}>{r.ip}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="row row-gap-3" style={{ justifyContent: 'flex-end', borderTop: '1px solid var(--bd)', paddingTop: 'var(--s3)' }}>
          {isSuperAdmin && (
            <button type="button" className="btn btn-primary" onClick={() => setShowSend(true)}>
              <Icon name="send" size={14} /> Send packet
            </button>
          )}
          <button type="button" className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>

      {showSend && (
        <SendPacketModal
          investor={data}
          docs={docs}
          onClose={() => setShowSend(false)}
          onCreated={() => { reload(); onChanged?.(); }}
        />
      )}
    </Modal>
  );
};

const Field = ({ label, children }) => (
  <div>
    <div className="text-xs" style={{ color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{label}</div>
    <div className="text-sm" style={{ color: 'var(--tx1)' }}>{children || '—'}</div>
  </div>
);

// ── Investors tab ──
const Investors = ({ docs, isSuperAdmin }) => {
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tierFilter, setTierFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    fetchInvestors()
      .then((d) => setInvestors(d.investors || []))
      .catch((e) => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => investors.filter((inv) => {
    if (tierFilter !== 'all' && (inv.tier || '').toLowerCase() !== tierFilter) return false;
    if (statusFilter !== 'all' && inv.outreach_status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = `${inv.name || ''} ${inv.primary_contact_name || ''} ${inv.email || ''} ${inv.geography || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }), [investors, tierFilter, statusFilter, search]);

  const tiers = useMemo(() => {
    const set = new Set();
    investors.forEach((i) => { if (i.tier) set.add(i.tier.toLowerCase()); });
    return Array.from(set).sort();
  }, [investors]);

  return (
    <>
      {error && <div role="alert" style={errorStyle}>{error}</div>}

      <div className="row row-gap-3 mb-3" style={{ flexWrap: 'wrap' }}>
        <input
          placeholder="Search investors…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...selectStyle, flex: '1 1 200px', minWidth: 200, maxWidth: 360, cursor: 'text' }}
        />
        <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)} style={selectStyle}>
          <option value="all">All tiers</option>
          {tiers.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={selectStyle}>
          <option value="all">All statuses</option>
          {Object.entries(STATUS_BADGE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <div className="text-sm" style={{ color: 'var(--tx3)', marginLeft: 'auto' }}>{filtered.length} of {investors.length}</div>
      </div>

      {loading ? (
        <div className="col col-gap-2">{[0,1,2].map((i) => <div key={i} className="skeleton skeleton-block" style={{ height: 50 }} />)}</div>
      ) : filtered.length === 0 ? (
        <div className="empty">
          <div className="empty-icon"><Icon name="folder" size={26} /></div>
          <h3>No investors match</h3>
          <p>Adjust your filters or run scripts/seed-investors.js to populate the CRM.</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ color: 'var(--tx3)', textAlign: 'left', borderBottom: '1px solid var(--bd)' }}>
                <th style={th}>#</th>
                <th style={th}>Name</th>
                <th style={th}>Tier</th>
                <th style={th}>Contact</th>
                <th style={th}>Status</th>
                <th style={th}>Last engaged</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((inv) => {
                const sb = STATUS_BADGE[inv.outreach_status] || STATUS_BADGE.not_started;
                return (
                  <tr key={inv.id} style={{ borderBottom: '1px solid var(--bd)' }}>
                    <td style={td}>{inv.crm_number}</td>
                    <td style={{ ...td, color: 'var(--tx1)', fontWeight: 600 }}>{inv.name}</td>
                    <td style={td}>{inv.tier || '—'}</td>
                    <td style={td}>{inv.primary_contact_name || '—'}</td>
                    <td style={td}><span className={`badge ${sb.cls}`}>{sb.label}</span></td>
                    <td style={td}>{inv.last_engaged ? fmtAgo(inv.last_engaged) : '—'}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <button type="button" className="btn btn-ghost" onClick={() => setSelectedId(inv.id)}>Open</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selectedId && (
        <InvestorDetail
          investorId={selectedId}
          docs={docs}
          onClose={() => setSelectedId(null)}
          onChanged={load}
          isSuperAdmin={isSuperAdmin}
        />
      )}
    </>
  );
};

// ── Access log tab ──
const AccessLogTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchAccessLog({ limit: 200 })
      .then((d) => { if (!cancelled) setRows(d.rows || []); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.error || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <>
      {error && <div role="alert" style={errorStyle}>{error}</div>}
      {loading ? (
        <div className="col col-gap-2">{[0,1,2].map((i) => <div key={i} className="skeleton skeleton-block" style={{ height: 36 }} />)}</div>
      ) : rows.length === 0 ? (
        <div className="empty">
          <div className="empty-icon"><Icon name="clock" size={26} /></div>
          <h3>No access events yet</h3>
          <p>Access events appear here as soon as someone opens a packet link.</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ color: 'var(--tx3)', textAlign: 'left', borderBottom: '1px solid var(--bd)' }}>
                <th style={th}>Time</th>
                <th style={th}>Investor</th>
                <th style={th}>Document</th>
                <th style={th}>Event</th>
                <th style={th}>IP</th>
                <th style={th}>UA</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderBottom: '1px solid var(--bd)' }}>
                  <td style={td}>{new Date(r.accessed_at).toLocaleString()}</td>
                  <td style={{ ...td, color: 'var(--tx1)', fontWeight: 600 }}>{r.investor_name || r.investor_id}</td>
                  <td style={td}>{r.document_filename || (r.event === 'index_view' ? '(index page)' : '—')}</td>
                  <td style={td}><span className="badge badge-info">{r.event}</span></td>
                  <td style={{ ...td, fontFamily: 'monospace' }}>{r.ip || '—'}</td>
                  <td style={{ ...td, color: 'var(--tx3)', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.user_agent || ''}>{r.user_agent || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

// ── Documents page (top-level) ──
const TABS = [
  { id: 'library',  label: 'Library' },
  { id: 'packets',  label: 'Investor packets' },
  { id: 'access',   label: 'Access log' },
];

const Documents = () => {
  const [activeTab, setActiveTab] = useState('library');
  const [docs, setDocs] = useState([]);
  const { isSuperAdmin } = usePagePermissions();

  useEffect(() => {
    fetchDocuments().then((d) => setDocs(d.documents || [])).catch(() => {});
  }, []);

  return (
    <>
      <div className="page-head">
        <h1>Documents</h1>
        <p>Tier 0/1 data room · investor packets · access log</p>
      </div>

      <div className="card">
        <div className="row" data-tabs="true" style={{ gap: 4, borderBottom: '1px solid var(--bd)', marginBottom: 16 }}>
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '10px 16px', background: 'transparent',
                color: activeTab === tab.id ? 'var(--teal)' : 'var(--tx2)',
                fontWeight: activeTab === tab.id ? 700 : 500,
                fontSize: 13,
                borderBottom: activeTab === tab.id ? '2px solid var(--teal)' : '2px solid transparent',
                marginBottom: -1, cursor: 'pointer',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'library' && <Library />}
        {activeTab === 'packets' && <Investors docs={docs} isSuperAdmin={isSuperAdmin} />}
        {activeTab === 'access'  && <AccessLogTab />}
      </div>
    </>
  );
};

const errorStyle = {
  background: 'var(--err-bg)',
  color: 'var(--err)',
  padding: 'var(--s3)',
  borderRadius: 'var(--r-md)',
  fontSize: 13,
  marginBottom: 'var(--s3)',
};

const selectStyle = {
  padding: '10px 14px',
  background: 'var(--bg-elevated)',
  border: '1px solid var(--bd)',
  borderRadius: 'var(--r-md)',
  color: 'var(--tx1)',
  fontSize: 13,
  fontFamily: 'var(--font)',
  cursor: 'pointer',
  outline: 'none',
};

const th = { padding: 'var(--s3) var(--s3)', fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' };
const td = { padding: 'var(--s3) var(--s3)', color: 'var(--tx2)' };

export default Documents;
